<template>
    <div>
        <div class="md:grid md:grid-cols-6 md:gap-6 px-4">
            <div class="sm:col-span-1">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                    {{ $t('messages.display_as') }}
                </label>
                <div class="mt-1">
                    <div
                        class="inline-flex items-center"
                        v-for="displayOption in displayOptions"
                        :key="displayOption.value"
                    >
                        <input
                            name="display_option"
                            :id="`display_option_${displayOption.value}`"
                            :value="displayOption.value"
                            v-model="show"
                            type="radio"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                        />
                        <label class="ml-1 mr-3" :for="`display_option_${displayOption.value}`">
                            <span class="block text-sm leading-5 font-medium text-gray-700">
                                {{ displayOption.name }}
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex justify-end">
            <div>
                <button @click="downloadCsv" :title="$t('messages.download')">
                    <IconDownload class="text-gray-400 mb-2 mr-1" />
                </button>
            </div>
        </div>
        <table class="min-w-full divide-y divide-gray-200 relative">
            <thead class="sticky top-0 bg-gray-50 z-10">
                <tr>
                    <th scope="col" class="sticky-top-0 left-0 z-10 bg-gray-50 px-1 py-2">&nbsp;</th>
                    <th
                        scope="col"
                        class="sticky-top-0 bg-gray-50 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        &nbsp;
                    </th>
                    <th
                        scope="col"
                        class="sticky-top-0 bg-gray-50 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        &nbsp;
                    </th>
                    <th
                        scope="col"
                        v-for="(category, index) in categories"
                        :colspan="category.dimensions_count + 1 + 1"
                        :key="category.id"
                        :class="{ 'bg-gray-200': index % 2 === 0, 'bg-gray-50': index % 2 !== 0 }"
                        class="sticky-top-0 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        {{ category.name }}
                    </th>
                </tr>
                <tr>
                    <th scope="col" class="sticky-top-0 left-0 z-10 bg-gray-50 px-1 py-2">
                        <span class="sr-only">{{ $t('messages.avatar') }}</span>
                    </th>
                    <th
                        scope="col"
                        class="sticky-top-0 bg-gray-50 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        {{ $t('messages.name') }}
                    </th>
                    <th
                        scope="col"
                        class="sticky-top-0 bg-gray-50 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        &nbsp;
                    </th>
                    <th
                        scope="col"
                        class="sticky-top-0 bg-gray-50 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                        {{ $t('messages.absenteeism') }}
                    </th>
                    <template v-for="(category, index) in categories" :key="category.id">
                        <th
                            scope="col"
                            v-for="dimension in category.dimensions"
                            :key="dimension.id"
                            class="sticky-top-0 text-center px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            :class="{ 'bg-gray-200': index % 2 === 0, 'bg-gray-50': index % 2 !== 0 }"
                        >
                            <span class="break-normal" :title="dimension.title">
                                {{ dimension.abbreviation }}
                            </span>
                        </th>
                        <th
                            scope="col"
                            class="sticky-top-0 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-bold"
                            :class="{ 'bg-gray-200': index % 2 === 0, 'bg-gray-50': index % 2 !== 0 }"
                        >
                            {{ $t('messages.global_abbreviation') }}
                        </th>
                        <th
                            scope="col"
                            class="sticky-top-0 px-1"
                            :class="{ 'bg-gray-200': index % 2 === 0, 'bg-gray-50': index % 2 !== 0 }"
                        >
                            &nbsp;
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
                <tr class="even:bg-gray-50" v-for="student in students">
                    <td class="sticky left-0 px-2 py-1 whitespace-nowrap sticky left-0 bg-gray-50">
                        <div class="flex items-center">
                            <div class="shrink-0 h-6 w-6">
                                <img
                                    :alt="student.student.full_name"
                                    :title="student.student.full_name"
                                    class="h-6 w-6 rounded-full"
                                    :src="student.student.avatar_or_default"
                                />
                            </div>
                        </div>
                    </td>
                    <td class="px-2 py-1 whitespace-nowrap text-sm text-gray-500">
                        <InertiaLink :href="student.student.links.show" class="text-indigo-500">
                            {{ student.student.full_name }}
                        </InertiaLink>
                    </td>
                    <td class="px-2 py-1 whitespace-nowrap text-sm text-gray-500 border-r-2 border-white">
                        <div v-if="student.adaptations.has_adaptations">
                            <Tippy>
                                <IconLifebuoy class="w-6 h-6 text-indigo-500" />
                                <template #content>
                                    <ul>
                                        <li v-for="tag in student.adaptations.tags" :key="tag.id">
                                            {{ tag.name }}
                                        </li>
                                    </ul>
                                    <br />
                                    <template v-if="student.adaptations.subjects.length">
                                        <div>{{ $tc('messages.adaptations', 2) }}</div>
                                        <ul class="pl-2">
                                            <li v-for="subject in student.adaptations.subjects" :key="subject.id">
                                                {{ subject.name }} - {{ subject.degree }}
                                            </li>
                                        </ul>
                                    </template>
                                </template>
                            </Tippy>
                        </div>
                    </td>
                    <td class="px-2 py-1 whitespace-nowrap text-sm text-gray-500 border-r-2 border-white">
                        <span v-if="student.attendance_percent">{{ student.attendance_percent }}%</span>
                    </td>
                    <template v-for="category in student.categories">
                        <template v-for="(dimension, dimensionIndex) in category.dimensions" :key="dimension.id">
                            <td
                                class="px-2 py-1 text-center whitespace-nowrap text-sm cursor-pointer"
                                @click="
                                    openStudentGradesPanel(student.student, category, `dimension_${dimensionIndex}`)
                                "
                                :class="[
                                    {
                                        'border-dashed border-2 border-indigo-500':
                                            dimension.final !== null && !dimension.final.is_calculated,
                                        'border-dashed border-2 border-gray-500 ':
                                            dimension.final !== null && dimension.final.is_calculated,
                                        'border-dashed border-2 border-gray-500': isLocked && dimension.final === null,
                                    },
                                    dimension.final === null ? dimension.classes : dimension.final.classes,
                                ]"
                            >
                                <template v-if="dimension.final === null">
                                    {{ dimension[show] }}
                                </template>
                                <template v-else>
                                    {{ dimension.final[show] }}
                                </template>
                            </td>
                        </template>
                        <td
                            class="px-2 py-1 text-center whitespace-nowrap text-sm border-r-2 cursor-pointer font-bold text-lg relative"
                            :class="[
                                {
                                    'border-dashed border-2 border-indigo-500':
                                        category.global.final.score !== null && !category.global.final.is_calculated,
                                    'border-dashed border-2 border-gray-500 ': category.global?.final?.is_calculated,
                                    'border-dashed border-2 border-gray-500':
                                        isLocked && category.global.final.score === null,
                                    'border-white': category.global.final.score === null,
                                },
                                category.global.final.score === null
                                    ? category.global.classes
                                    : category.global.final.classes,
                            ]"
                            @click="openStudentGradesPanel(student.student, category, `dimension_global`)"
                        >
                            <template v-if="category.global.final?.is_not_graded">
                                <div class="flex justify-center" :title="$t('messages.is_not_graded')">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="text-gray-400"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                        <line x1="9" y1="15" x2="15" y2="9"></line>
                                    </svg>
                                </div>
                            </template>
                            <template v-else-if="category.global.final.score !== null">
                                {{ category.global.final[show] }}
                            </template>
                            <template v-else>
                                {{ category.global[show] }}
                            </template>
                            <div
                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-gray-300 absolute right-1 bottom-1"
                                v-if="category.global.final.comment"
                            />
                        </td>
                        <td></td>
                    </template>
                </tr>
            </tbody>
            <tfoot>
                <tr class="even:bg-gray-50" v-for="(data, key) in acc">
                    <td class="sticky left-0 px-2 py-1 whitespace-nowrap sticky left-0 bg-gray-50">
                        {{ key }}
                    </td>
                    <td class="px-2 py-1 whitespace-nowrap text-sm text-gray-500">
                        {{ data.text }}
                    </td>
                    <td class="px-2 py-1 whitespace-nowrap text-sm text-gray-500">&nbsp;</td>
                    <template v-for="category in data.categories">
                        <td
                            v-for="dimension in category.dimensions"
                            class="px-2 py-1 text-center whitespace-nowrap text-sm text-gray-500"
                            :class="dimension.colors"
                        >
                            <span :class="{ 'text-gray-300': dimension === 0 }">{{ dimension }}</span>
                        </td>
                        <td
                            class="px-2 py-1 text-center whitespace-nowrap text-sm text-gray-500 bg-white border-r-2 border-white"
                            :class="category.global.colors"
                        >
                            <span
                                :class="{
                                    'text-gray-300': category.global === 0,
                                    'font-bold': category.global !== 0,
                                }"
                                >{{ category.global }}</span
                            >
                        </td>
                        <td></td>
                    </template>
                </tr>
            </tfoot>
        </table>
        <FinalTrimesterGradesFormPanel
            :student="selectedStudent"
            :category-grades="categoryGrades"
            :selector="selector"
            :category="selectedCategory"
            v-if="selectedStudent !== null"
            :can-edit="canEdit"
            @close="closeFinalTrimesterGradesFormPanel()"
            :ai-enabled="aiEnabled"
        />
    </div>
</template>

<script>
import FinalTrimesterGradesFormPanel from '@/components/FinalTrimesterGradesFormPanel.vue';
import { IconDownload, IconLifebuoy } from '@tabler/icons-vue';
import BadgePill from '@/components/BadgePill.vue';
import { Tippy } from 'vue-tippy';

export default {
    name: 'TrimesterGradesTable',
    props: ['students', 'categories', 'acc', 'canEdit', 'isLocked', 'aiEnabled'],
    components: {
        Tippy,
        BadgePill,
        FinalTrimesterGradesFormPanel,
        IconDownload,
        IconLifebuoy,
    },
    data() {
        return {
            displayColors: true,
            selectedStudent: null,
            selectedCategory: null,
            categoryGrades: null,
            selector: null,
            show: 'letter',
            displayOptions: [
                { name: this.$tc('messages.scores', 2), value: 'score' },
                { name: this.$tc('messages.letters', 2), value: 'letter' },
            ],
        };
    },
    methods: {
        openStudentGradesPanel(student, category, selector) {
            this.categoryGrades = category;
            this.selector = selector;
            this.selectedCategory = this.categories.find((c) => c.id == category.id);
            this.selectedStudent = student;
        },
        closeFinalTrimesterGradesFormPanel() {
            this.categoryGrades = null;
            this.selectedStudent = null;
            this.selectedCategory = null;
        },
        toArray(isCsv = false) {
            const array = [];
            let row = [];
            //first row
            row.push('');
            this.categories.forEach((category) => {
                row.push(category.name);
                for (let i = 0; i < category.dimensions.length; i++) {
                    row.push('');
                }
            });
            array.push(row);
            row = [];
            //second row
            row.push(this.$t('messages.name'));
            this.categories.forEach((category) => {
                category.dimensions.forEach((dimension) => {
                    row.push(dimension.abbreviation);
                });
                row.push('G');
            });
            array.push(row);

            //student scores
            this.students.forEach((student) => {
                row = [];
                const fullName = student.student.full_name;
                row.push(isCsv ? `"${fullName}"` : fullName);
                student.categories.forEach((category) => {
                    category.dimensions.forEach((dimension) => {
                        if (dimension.final) {
                            row.push(dimension.final.letter);
                        } else {
                            row.push(dimension.letter);
                        }
                    });

                    const final = category.global.final.letter;
                    if (final) {
                        row.push(final);
                    } else {
                        row.push(category.global.letter);
                    }
                });
                array.push(row);
            });

            for (let [, value] of Object.entries(this.acc)) {
                row = [];
                row.push(value.text);
                value.categories.forEach((category) => {
                    category.dimensions.forEach((dimension) => {
                        row.push(dimension);
                    });

                    row.push(category.global);
                });
                array.push(row);
            }

            return array;
        },
        toCsv() {
            const array = this.toArray(true);
            const rows = [];
            array.forEach((row) => {
                rows.push(row.join(','));
            });

            return rows.join('\n');
        },
        downloadCsv() {
            const data = this.toCsv();
            // Creating a Blob for having a csv file format
            // and passing the data with type
            const blob = new Blob([data], { type: 'text/csv' });

            // Creating an object for downloading url
            const url = window.URL.createObjectURL(blob);

            // Creating an anchor(a) tag of HTML
            const a = document.createElement('a');

            // Passing the blob downloading url
            a.setAttribute('href', url);

            // Setting the anchor tag attribute for downloading
            // and passing the download file name
            a.setAttribute('download', this.$tc('messages.grades', 2) + '.csv');

            // Performing a download with click
            a.click();

            this.copyToClipboard();
        },
        copyToClipboard() {
            const array = this.toArray();

            const rows = [];
            array.forEach((row) => {
                rows.push(row.join('\t'));
            });

            const string = rows.join('\n');
            const clipboard = navigator.clipboard;
            if (clipboard === undefined) {
            } else {
                clipboard.writeText(string);
            }
        },
    },
};
</script>
